/**
 * How to play, and what just happened to you.
 *
 * Two things live here because they are the same problem: the game knew
 * something the player did not. The first person to play it walked up to a
 * beast, was killed by it, woke up at the town gate, and had no way of finding
 * out why — there was a two-second toast and nothing else. That is not a
 * difficulty problem, it is a *reporting* problem, and no amount of balancing
 * fixes it.
 *
 * The key list is built from the shared bindings rather than typed out again,
 * so a help screen that says "press E" is a help screen where E is really the
 * key. See packages/shared/src/controls.ts.
 */

import {
  ACTION_BINDINGS, MOUSE_CONTROLS, MOVEMENT_BINDINGS, TOUCH_CONTROLS
} from '@emberreach/shared';

const SEEN_KEY = 'emberreach.help.seen';

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A titled block of prose. */
function section(title: string, paragraphs: string[]): HTMLElement {
  const wrap = h('section', 'help-section');
  wrap.append(h('h3', undefined, title));
  for (const p of paragraphs) wrap.append(h('p', undefined, p));
  return wrap;
}

/** A titled two-column table of control → what it does. */
function table(title: string, rows: { label: string; does: string }[]): HTMLElement {
  const wrap = h('section', 'help-section');
  wrap.append(h('h3', undefined, title));

  const list = h('dl', 'help-keys');
  for (const row of rows) {
    list.append(h('dt', undefined, row.label));
    list.append(h('dd', undefined, row.does));
  }
  wrap.append(list);
  return wrap;
}

export class HelpScreen {
  private readonly root: HTMLDivElement;
  private lastFocused: HTMLElement | null = null;

  constructor(parent: HTMLElement, touch: boolean) {
    this.root = h('div', 'sheet-backdrop');
    this.root.hidden = true;

    const card = h('div', 'sheet-card help-card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', 'How to play');

    const head = h('div', 'sheet-head');
    head.append(h('h2', undefined, 'How to play'));
    const close = h('button', 'sheet-close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', () => this.close());
    head.append(close);
    card.append(head);

    const body = h('div', 'sheet-body');

    body.append(section('What this is', [
      'Emberreach is a small world with two towns in it, and other real people ' +
      'walking around in it at the same time as you. There is no ending to ' +
      'reach and nothing you can permanently lose.',
      'What there is: things to kill, things to sell, people to talk to, and a ' +
      'second town along the road that looks nothing like the first.'
    ]));

    body.append(section('What to do first', [
      '1. Talk to somebody. Anyone standing under a name is worth a word, and ' +
      'the ones with a gold marker over their head will trade with you.',
      '2. Look at what you are carrying. You start with a weapon, something to ' +
      'wear and a bit of food — it is all already on you.',
      '3. Go out past the edge of town and fight something small. Keep an eye ' +
      'on the level next to its name.',
      '4. Bring what it drops back and sell it.'
    ]));

    body.append(table('Moving about', MOVEMENT_BINDINGS.map((b) => ({
      label: b.label, does: b.does
    }))));

    body.append(table(touch ? 'Touch' : 'The mouse',
      touch ? TOUCH_CONTROLS : MOUSE_CONTROLS));

    body.append(table('Everything else', ACTION_BINDINGS.map(({ binding }) => ({
      label: binding.label, does: binding.does
    }))));

    body.append(section('Fighting', [
      'Every creature has a level next to its name. If that number is higher ' +
      'than yours it will probably win — a level 4 beast against a level 1 ' +
      'character is not a fair fight and it is not meant to be.',
      'They notice you from about ten metres and will chase. Walk far enough ' +
      'away and they give up and go home.',
      'Your health is the red bar, top left. It comes back on its own when ' +
      'nothing is hitting you, and faster if you eat something.'
    ]));

    body.append(section('If you are killed', [
      'You wake up at the town spawn point with half your health back. That is ' +
      'the whole penalty.',
      'You keep every item, every Mark and all your experience. Nothing is ' +
      'taken off you for dying, so there is no reason not to go and have a look ' +
      'at something.'
    ]));

    body.append(section('Signs and offers', [
      'The billboards and shop signs around the towns carry real advertisements ' +
      'from real businesses. Walk up to one and press E to read it.',
      'Nothing in the game will ever open a web page or take you out of the ' +
      'world. If an offer is worth keeping, keep it — it will be waiting on ' +
      'your offers page when you have finished playing.'
    ]));

    body.append(section('Other people', [
      'Everyone you see with a name over their head is another player, in the ' +
      'same town as you, right now. Press Enter to say something to all of them.',
      'Keep it civil. Chat is logged and it can be reported.'
    ]));

    card.append(body);

    const foot = h('div', 'sheet-foot');
    const got = h('button', 'primary', 'Right, got it');
    got.type = 'button';
    got.addEventListener('click', () => this.close());
    foot.append(got);
    foot.append(h('p', 'tiny muted', touch
      ? 'Tap the ? button any time to see this again.'
      : 'Press H at any time to see this again.'));
    card.append(foot);

    this.root.append(card);
    parent.append(this.root);

    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (!this.root.hidden && event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    });
  }

  get isOpen(): boolean { return !this.root.hidden; }

  open(): void {
    if (!this.root.hidden) return;
    this.lastFocused = document.activeElement as HTMLElement | null;
    this.root.hidden = false;
    this.root.querySelector<HTMLButtonElement>('button.primary')?.focus();
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.lastFocused?.focus();
  }

  toggle(): void { this.isOpen ? this.close() : this.open(); }

  /**
   * True the first time somebody plays. Reading localStorage can throw in
   * private browsing, and a help screen is not worth an exception — a player who
   * gets it twice is fine, a player who gets a crash is not.
   */
  static neverSeen(): boolean {
    try { return localStorage.getItem(SEEN_KEY) === null; } catch { return false; }
  }
}

export interface DeathDetails {
  /** What killed them, e.g. "Moorstalker". */
  killedBy: string;
  /** Its level, or null when the server did not say. */
  enemyLevel: number | null;
  /** Theirs, for the comparison that explains the whole thing. */
  yourLevel: number;
  /** Where they will wake up. */
  placeName: string;
  /** Seconds until they are back up. */
  seconds: number;
}

/**
 * The screen that answers "why am I back at the start".
 *
 * Deliberately a full overlay with a countdown rather than a toast: the player
 * is not in control for these few seconds anyway, and this is the one moment
 * where they most need to be told something.
 */
export class DeathNotice {
  private readonly root: HTMLDivElement;
  private readonly headline: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly advice: HTMLElement;
  private readonly counter: HTMLElement;
  private timer: number | null = null;
  private remaining = 0;

  constructor(parent: HTMLElement) {
    this.root = h('div', 'death-backdrop');
    this.root.hidden = true;

    const card = h('div', 'death-card');
    card.setAttribute('role', 'alertdialog');
    card.setAttribute('aria-live', 'assertive');

    this.headline = h('h2', undefined, 'You were killed');
    this.detail = h('p', 'death-detail');
    this.advice = h('p', 'death-advice');
    this.counter = h('p', 'death-counter');

    card.append(this.headline, this.detail, this.advice, this.counter);

    const keep = h('p', 'death-keep',
      'You keep everything you were carrying. No items, Marks or experience are lost.');
    card.append(keep);

    this.root.append(card);
    parent.append(this.root);
  }

  get isOpen(): boolean { return !this.root.hidden; }

  show(details: DeathDetails): void {
    this.headline.textContent = `A ${details.killedBy} killed you`;
    // The headline already named it; repeating it here just made the sentence
    // read like a form letter.
    this.detail.textContent =
      (details.enemyLevel === null ? '' : `It was level ${details.enemyLevel}. `) +
      `You are level ${details.yourLevel}. ` +
      `You will wake up at ${details.placeName}.`;

    this.advice.textContent =
      details.enemyLevel !== null && details.enemyLevel > details.yourLevel
        ? 'That one is above your level. Leave it alone until you have caught up — ' +
          'the small ones near town are worth the same experience for far less trouble.'
        : 'Keep an eye on your health bar, and back off out of its reach when it drops low.';

    this.remaining = Math.max(1, Math.round(details.seconds));
    this.paint();
    this.root.hidden = false;

    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = window.setInterval(() => {
      this.remaining -= 1;
      if (this.remaining <= 0) this.paint();
      else this.paint();
    }, 1000);
  }

  private paint(): void {
    this.counter.textContent = this.remaining > 0
      ? `Back on your feet in ${this.remaining}…`
      : 'Getting up…';
  }

  hide(): void {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    this.root.hidden = true;
  }
}
